import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink } from 'lucide-react';
import type { Intervention, InterventionEvent } from '../../generated/api/types.gen.js';
import { getInterventionOptions } from '../../generated/api/@tanstack/react-query.gen.js';
import { Badge, Card, DataTable, QueryBoundary } from '../../components/ui/index.js';
import type { DataTableColumn } from '../../components/ui/index.js';
import { DetailPage } from '../../components/page/DetailPage.js';
import { buttonStyles } from '../../components/tailgrids/core/button.js';
import { safeHref } from '../../lib/safe-url.js';
import { PAGE_HELP } from '../../help/index.js';
import { ActionRequestEvidence } from './ActionRequestEvidence.js';
import { ActionRequestRoutePanel } from './ActionRequestRoutePanel.js';
import { SEVERITY_VARIANT, STATUS_VARIANT } from './action-request-labels.js';
import { formatDateTime, humanize } from './admin-format.js';

const TIMELINE_COLUMNS: DataTableColumn<InterventionEvent>[] = [
  {
    key: 'when',
    header: 'When',
    width: '190px',
    render: (event) => <span className="whitespace-nowrap text-sm text-muted">{formatDateTime(event.createdAt)}</span>,
  },
  {
    key: 'event',
    header: 'Event',
    render: (event) => (
      <div>
        <span className="text-sm font-medium text-heading">{humanize(event.eventType)}</span>
        {event.message && <p className="text-sm text-muted">{event.message}</p>}
      </div>
    ),
  },
];

function SourceLink({ request }: { request: Intervention }) {
  // The source URL is built on the server from the source record; it is still checked before it becomes a link.
  const href = safeHref(request.sourceUrl);
  if (!href) return null;
  return (
    <Link to={href} className={buttonStyles({ variant: 'primary', appearance: 'outline', size: 'sm' })}>
      <ExternalLink size={14} aria-hidden="true" />
      Open source
    </Link>
  );
}

export const ActionRequestDetailPage: React.FC = () => {
  const { id } = useParams();
  const query = useQuery({ ...getInterventionOptions({ path: { id: id ?? '' } }), enabled: !!id });
  const request = query.data?.request;

  return (
    <DetailPage
      title={request?.title ?? 'Action request'}
      subtitle={request?.reason}
      breadcrumbs={[{ label: 'Action requests', to: '/action-requests' }, { label: request?.title ?? 'Request' }]}
      helpContent={PAGE_HELP['action-requests']}
      badges={
        request && (
          <>
            <Badge variant={STATUS_VARIANT[request.status]} dot>
              {humanize(request.status)}
            </Badge>
            <Badge variant={SEVERITY_VARIANT[request.severity]}>{humanize(request.severity)}</Badge>
          </>
        )
      }
      actions={request && <SourceLink request={request} />}
      meta={
        request && (
          <p className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted">
            <span>
              Source <span className="text-heading">{request.sourceName}</span> ({humanize(request.sourceType)})
            </span>
            <span>
              Type <span className="text-heading">{humanize(request.type)}</span>
            </span>
            <span>
              Assigned <span className="text-heading">{request.assignedToName ?? 'Unassigned'}</span>
            </span>
            <span>
              Opened <span className="text-heading">{formatDateTime(request.createdAt)}</span>
            </span>
          </p>
        )
      }
      rail={request && <ActionRequestRoutePanel key={request.id} request={request} />}
    >
      <QueryBoundary query={query} label="action request">
        {({ request: loaded, timeline }) => (
          <>
            <Card title="Evidence">
              <ActionRequestEvidence evidence={loaded.evidence} />
            </Card>
            <Card title="Timeline">
              <DataTable
                columns={TIMELINE_COLUMNS}
                rows={timeline}
                rowKey={(event) => event.id}
                aria-label="Request timeline"
              />
            </Card>
            <Card title="Linked outcomes">
              {loaded.linkedOutcomes.length === 0 ? (
                <p className="text-sm text-muted">No outcomes are linked yet.</p>
              ) : (
                <ul className="space-y-2">
                  {loaded.linkedOutcomes.map((item, index) => {
                    const href = typeof item.url === 'string' ? safeHref(item.url) : null;
                    return (
                      <li key={`${String(item.type)}-${index}`} className="rounded-lg border bg-surface-alt px-3 py-2">
                        <p className="text-sm font-medium text-heading">
                          {String(item.label ?? item.type ?? 'Outcome')}
                        </p>
                        {href && (
                          <Link to={href} className="text-xs text-accent-text hover:underline">
                            Open outcome
                          </Link>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </Card>
          </>
        )}
      </QueryBoundary>
    </DetailPage>
  );
};

export default ActionRequestDetailPage;
